import type { BlogSectionContent } from "@site-factory/config";
import { SectionShell } from "../components/SectionShell";
import { SectionTitle, SectionSubtitle } from "../typography/Typography";
import { Card, CardContent } from "../components/Card";
import { Button } from "../components/Button";
import { cn } from "../utils";
import { OptimizedImage } from "../components/OptimizedImage";

export interface BlogSectionProps extends BlogSectionContent {
  className?: string;
}

export function BlogSection({
  title,
  subtitle,
  posts,
  variant = "grid",
  columns = 3,
  className,
}: BlogSectionProps) {
  const gridCols =
    columns === 2
      ? "md:grid-cols-2"
      : columns === 3
      ? "md:grid-cols-2 lg:grid-cols-3"
      : "md:grid-cols-2 lg:grid-cols-3";
  const masonryCols = columns === 2 ? "md:grid-cols-2 lg:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3";

  return (
    <SectionShell className={className} backgroundClassName="bg-surface">
      {(title || subtitle) && (
        <div className="text-center mb-12">
          {title && <SectionTitle>{title}</SectionTitle>}
          {subtitle && <SectionSubtitle className="mt-4">{subtitle}</SectionSubtitle>}
        </div>
      )}

      <div
        className={cn(
          variant === "grid"
            ? `grid gap-8 ${gridCols}`
            : variant === "masonry"
            ? `grid gap-6 ${masonryCols}`
            : "space-y-6"
        )}
      >
        {posts.map((post: BlogSectionContent["posts"][number]) => (
          <Card
            key={post.id}
            className="overflow-hidden h-full border border-[color:var(--color-text,#0f172a)]/12 bg-[var(--color-surface,#fff)] text-[var(--color-text,#0f172a)] shadow-sm-2 hover:shadow-md-2 transition"
            style={variant === "masonry" ? { breakInside: "avoid" } : undefined}
          >
            {post.image && (
              <div className="w-full aspect-video overflow-hidden">
                <OptimizedImage
                  src={post.image}
                  alt={post.title}
                  aspectRatio="16/9"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <CardContent className="p-6 space-y-3">
              <div
                className="text-xs uppercase tracking-wide"
                style={{ color: "var(--color-text,#0f172a)", opacity: 0.65 }}
              >
                {post.category || "Article"} • {post.date}
              </div>
              <h3 className="text-lg font-semibold">{post.title}</h3>
              {post.excerpt && (
                <p
                  className="text-sm"
                  style={{ color: "var(--color-text,#0f172a)", opacity: 0.82 }}
                >
                  {post.excerpt}
                </p>
              )}
              <div
                className="flex items-center justify-between text-sm"
                style={{ color: "var(--color-text,#0f172a)", opacity: 0.75 }}
              >
                {post.author && <span>{post.author}</span>}
                {post.readTime && <span>{post.readTime}</span>}
              </div>
              {post.href && (
                <div className="pt-2">
                  <Button asChild variant="soft" size="sm">
                    <a href={post.href} className="sf-link">
                      Read more
                    </a>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </SectionShell>
  );
}
